<script setup>
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const i18nScope = 'components.sales_analytics';

  const props = defineProps({
    data: {
      type: Array,
      default: () => []
    },
    comparisonData: {
      type: Array,
      default: () => []
    },
    showComparison: {
      type: Boolean,
      default: false
    }
  });

  const totalCount = computed(() => {
    return props.data.reduce((sum, item) => sum + item.count, 0);
  });

  const sortedData = computed(() => {
    return [...props.data].sort((a, b) => b.count - a.count).slice(0, 10);
  });

  const maxCount = computed(() => {
    if (sortedData.value.length === 0) return 1;
    return Math.max(...sortedData.value.map(d => d.count));
  });

  function getPercentage(count) {
    if (totalCount.value === 0) return 0;
    return ((count / totalCount.value) * 100).toFixed(1);
  }

  function getBarWidth(count) {
    return (count / maxCount.value) * 100;
  }

  function getPeerPercentage(key) {
    if (!props.showComparison || !props.comparisonData) return null;

    const peerTotal = props.comparisonData.reduce((sum, item) => sum + item.count, 0);
    const peerItem = props.comparisonData.find(item => item.key === key);

    if (!peerItem || peerTotal === 0) return null;
    return ((peerItem.count / peerTotal) * 100).toFixed(1);
  }
</script>

<template>
  <div class="category-breakdown">
    <div v-if="sortedData.length === 0" class="text-center text-muted py-3">
      {{ t(`${i18nScope}.no_data`) }}
    </div>

    <div v-else class="breakdown-list">
      <div v-for="item in sortedData" :key="item.key" class="breakdown-item mb-2">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="breakdown-label">{{ item.key }}</span>
          <span class="breakdown-value">
            {{ item.count.toLocaleString() }}
            <span class="text-muted">({{ getPercentage(item.count) }}%)</span>
            <span v-if="getPeerPercentage(item.key)" class="peer-comparison ms-2">
              <span class="text-muted">{{ t(`${i18nScope}.peers`) }}:</span>
              {{ getPeerPercentage(item.key) }}%
            </span>
          </span>
        </div>
        <div class="breakdown-bar-container">
          <div
            class="breakdown-bar"
            :style="{ width: getBarWidth(item.count) + '%' }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .category-breakdown {
    max-height: 400px;
    overflow-y: auto;
  }

  .breakdown-item {
    padding: 4px 0;
  }

  .breakdown-label {
    font-size: 0.875rem;
    font-weight: 500;
  }

  .breakdown-value {
    font-size: 0.875rem;
  }

  .peer-comparison {
    font-size: 0.75rem;
  }

  .breakdown-bar-container {
    height: 8px;
    background-color: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
  }

  .breakdown-bar {
    height: 100%;
    background-color: #0d6efd;
    border-radius: 4px;
    transition: width 0.3s ease;
  }
</style>
